import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import LocationPicker from "@/components/LocationPicker";
import CustomDatePicker from "@/components/DateTimePicker";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import ClientEditModal from "@/components/ClientEditModal";
import * as XLSX from "xlsx-js-style";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime } from "@/utils/date";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import NumberField from "@/components/NumberField";
import MultiPhoneInput, { hydratePhones, serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import {
  Buildings,
  Phone,
  MapPin,
  Check,
  X as XIcon,
  Prohibit,
  Clock,
  CheckCircle,
  Plus,
  Trash,
  Pencil,
  Tag as TagIcon,
  Car as CarIcon,
  MagnifyingGlass,
  DownloadSimple,
  Envelope,
  WhatsappLogo,
  FolderOpen,
  Warning,
} from "@phosphor-icons/react";

const STATUS_LABEL = { approved: "معتمدة", pending: "قيد المراجعة", rejected: "مرفوضة", blocked: "محظورة", admin: "أدمن" };
const STATUS_STYLE = {
  approved: "bg-[#2E7D32]/15 border-[#2E7D32]/50 text-[#2E7D32]",
  pending: "bg-[#F57F17]/15 border-[#F57F17]/50 text-[#F57F17]",
  rejected: "bg-[#C62828]/15 border-[#C62828]/50 text-[#C62828]",
  blocked: "bg-white/5 border-white/20 text-white/60",
  admin: "bg-primary/15 border-primary/50 text-primary",
};

function StatCard({ label, value, tone = "primary" }) {
  const toneClass =
    tone === "green" ? "text-[#2E7D32]" : tone === "amber" ? "text-[#F57F17]" : "text-primary";
  return (
    <div className="rounded-xl border border-primary/20 bg-[#0F2148] p-4">
      <div className="text-white/60 text-xs mb-1">{label}</div>
      <div className={`text-2xl font-bold num ${toneClass}`}>{fmtNum(value)}</div>
    </div>
  );
}

function PricingModal({ open, cars, companyId, onClose, onSaved }) {
  const [form, setForm] = useState({
    pickup: "",
    destination: "",
    car_type: "",
    price: 0,
    from_date: "",
    to_date: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ pickup: "", destination: "", car_type: "", price: 0, from_date: "", to_date: "", reason: "" });
    }
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup.trim() || !form.destination.trim()) return toast.error("الانطلاق والوجهة مطلوبان");
    if (!form.car_type) return toast.error("نوع السيارة مطلوب");
    if (!Number(form.price) || Number(form.price) <= 0) return toast.error("السعر مطلوب");
    setSaving(true);
    try {
      await api.post(`/admin/companies/${companyId}/custom-pricing`, {
        ...form,
        price: Number(form.price),
      });
      toast.success("تم إنشاء التسعير الخاص");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mz-modal-backdrop" onClick={onClose} dir="rtl" data-testid="custom-pricing-modal">
      <div className="mz-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mz-modal-header">
          <h3 className="text-primary font-bold text-lg">تسعير خاص للشركة</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="contents">
          <div className="mz-modal-body">
            <div className="mz-modal-grid">
              {/* Unified LocationPicker — chips + clean text input */}
              <LocationPicker
                label="نقطة الانطلاق *"
                value={form.pickup}
                onChange={(v) => set("pickup", v)}
                includeShrines={false}
                otherEndpointValue={form.destination}
                testIdPrefix="cp-pickup"
              />
              <LocationPicker
                label="الوجهة *"
                value={form.destination}
                onChange={(v) => set("destination", v)}
                includeShrines={true}
                pickupContext={form.pickup}
                otherEndpointValue={form.pickup}
                testIdPrefix="cp-destination"
              />

              <div>
                <div className="text-xs text-white/60 mb-1 text-right">نوع السيارة *</div>
                <div dir="rtl" className="flex flex-wrap gap-2" data-testid="cp-car-chips">
                  {cars.map((c) => {
                    const active = form.car_type === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => set("car_type", c.id)}
                        data-testid={`cp-car-chip-${c.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? "bg-primary text-primary-foreground border-primary font-bold"
                            : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                        }`}
                      >
                        {c.name_ar}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">السعر (ر.س) *</div>
                <NumberField required min={0} value={form.price} onChange={(val) => set("price", val)} className="mz-input" testId="cp-price" placeholder="0" />
              </label>

              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">من تاريخ (اختياري)</div>
                <CustomDatePicker
                  value={form.from_date}
                  onChange={(v) => set("from_date", v)}
                  placeholder="YYYY-MM-DD"
                  testId="cp-from"
                />
              </label>
              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">إلى تاريخ (اختياري)</div>
                <CustomDatePicker
                  value={form.to_date}
                  onChange={(v) => set("to_date", v)}
                  minDate={form.from_date ? new Date(`${form.from_date}T00:00:00`) : undefined}
                  placeholder="YYYY-MM-DD"
                  testId="cp-to"
                />
              </label>

              <label className="block mz-modal-grid-full">
                <div className="text-xs text-white/60 mb-1 text-right">السبب</div>
                <input value={form.reason} onChange={(e) => set("reason", e.target.value)} className="mz-input" data-testid="cp-reason" />
              </label>
            </div>
          </div>
          <div className="mz-modal-footer">
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="cp-save">
              {saving ? "جارٍ الحفظ…" : "حفظ"}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCars, setSavingCars] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSaving, setTemplateSaving] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, tpl] = await Promise.all([
        api.get(`/admin/companies/${id}`),
        api.get("/cars"),
        api.get("/admin/pricing-templates").catch(() => ({ data: { templates: [] } })),
      ]);
      setData(d.data);
      setCars(c.data);
      setTemplates((tpl.data && tpl.data.templates) || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setPricingTemplate = async (templateId) => {
    // templateId === null / "" → unlink. Otherwise pick that template.
    setTemplateSaving(true);
    try {
      await api.patch(`/admin/companies/${id}`, {
        pricing_template_id: templateId || "",
      });
      toast.success(templateId ? "تم ربط الشركة بالقالب" : "تم إلغاء الربط");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTemplateSaving(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/companies/${id}/status`, { status });
      toast.success("تم تحديث الحالة");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCar = async (carId) => {
    if (!data) return;
    const current = new Set(data.visible_cars || []);
    if (current.has(carId)) current.delete(carId);
    else current.add(carId);
    const next = Array.from(current);
    setSavingCars(true);
    // Optimistic
    setData((d) => (d ? { ...d, visible_cars: next } : d));
    try {
      await api.patch(`/admin/companies/${id}/visible-cars`, { car_ids: next });
      toast.success("تم تحديث السيارات المرئية");
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    } finally {
      setSavingCars(false);
    }
  };

  const removePricing = async (p) => {
    const ok = await confirm({
      title: "حذف التسعير الخاص",
      message: `سيتم حذف التسعير: ${p.pickup} → ${p.destination}`,
      confirmText: "احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/companies/${id}/custom-pricing/${p.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ─── Clients CRUD (admin) ────────────────────────────────────────
  const [clientsQ, setClientsQ] = useState("");
  const [clientEdit, setClientEdit] = useState(null); // {idx, initial} | null
  const [clientsBusy, setClientsBusy] = useState(false);
  const [phonesEditOpen, setPhonesEditOpen] = useState(false);
  const [phonesDraft, setPhonesDraft] = useState([{ number: "+966", is_primary: true }]);
  const [phonesSaving, setPhonesSaving] = useState(false);

  const clientsFiltered = useMemo(() => {
    const list = Array.isArray(data?.clients) ? data.clients : [];
    const s = clientsQ.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) =>
      (c.name || "").toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.phone1 || "").includes(s)
      || (c.phone2 || "").includes(s)
    );
  }, [data?.clients, clientsQ]);

  const saveClient = async (payload) => {
    setClientsBusy(true);
    try {
      if (clientEdit?.idx != null) {
        await api.put(`/admin/companies/${id}/clients/${clientEdit.idx}`, payload);
        toast.success("تم تحديث العميل");
      } else {
        await api.post(`/admin/companies/${id}/clients`, payload);
        toast.success("تم إضافة العميل");
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    } finally {
      setClientsBusy(false);
    }
  };

  const removeClient = async (idx, name) => {
    const ok = await confirm({
      title: "حذف العميل",
      message: `سيتم حذف "${name}" من قائمة عملاء الشركة.`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    setClientsBusy(true);
    try {
      await api.delete(`/admin/companies/${id}/clients/${idx}`);
      toast.success("تم الحذف");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClientsBusy(false);
    }
  };

  // Excel bulk import removed (feature deprecated).

  const exportClientsExcel = () => {
    const list = Array.isArray(data?.clients) ? data.clients : [];
    if (!list.length) return toast.error("لا يوجد عملاء للتصدير");
    const rows = [
      ["اسم العميل", "البريد الإلكتروني", "رقم الجوال الأول", "رقم الجوال الثاني"],
      ...list.map((c) => [c.name || "", c.email || "", c.phone1 || "", c.phone2 || ""]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 20 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "العملاء");
    XLSX.writeFile(wb, `${(data.name || "company").replace(/\s+/g, "_")}-clients.xlsx`);
    toast.success("تم التصدير");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground" dir="rtl">
        <TopBar />
        <div className="text-center py-24 text-primary animate-pulse">جارٍ التحميل…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-background text-foreground" dir="rtl">
        <TopBar />
        <div className="text-center py-24 text-white/50">الشركة غير موجودة</div>
      </div>
    );
  }

  const isAdminCompany = data.status === "admin";
  const carName = (cid) => cars.find((c) => c.id === cid)?.name_ar || cid;
  const visibleSet = new Set(data.visible_cars || []);
  const showAll = (data.visible_cars || []).length === 0;

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="company-detail">
        {/* Header */}
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Buildings size={30} weight="duotone" className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-white">{data.name}</h1>
              <span className={`text-[11px] px-2.5 py-1 rounded-full border ${STATUS_STYLE[data.status] || ""}`}>
                {STATUS_LABEL[data.status] || data.status}
              </span>
            </div>
            {data.contact_person_name ? (
              <div
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-semibold"
                data-testid="company-contact-person"
              >
                👤 مسؤول التواصل: {data.contact_person_name}
              </div>
            ) : (
              !isAdminCompany && (
                <button
                  type="button"
                  onClick={async () => {
                    const name = window.prompt("اسم مسؤول التواصل:");
                    if (!name || name.trim().length < 3) return;
                    try {
                      await api.patch(`/admin/companies/${id}`, { contact_person_name: name.trim() });
                      toast.success("تم حفظ اسم مسؤول التواصل");
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
                  }}
                  data-testid="company-add-contact-person"
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-dashed border-primary/40 text-primary/70 hover:border-primary hover:text-primary text-xs"
                >
                  + إضافة اسم مسؤول التواصل
                </button>
              )
            )}
            <div className="mt-1 text-white/60 text-sm">{data.email}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <div className="space-y-1">
                {(data.phones && data.phones.length > 0
                  ? data.phones
                  : data.phone ? [{ number: data.phone, is_primary: true }] : []
                ).map((p, pIdx) => {
                  const digits = String(p.number || "").replace(/\D+/g, "");
                  if (!digits) return null;
                  return (
                    <div key={pIdx} className="flex items-center gap-1.5 flex-wrap" dir="ltr">
                      <Phone size={13} className="text-primary/60" />
                      <span className="num text-white/70 text-sm">{formatPhoneDisplay(p.number)}</span>
                      {p.is_primary && (data.phones?.length > 1) && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary font-bold">★</span>
                      )}
                      <a href={`tel:${p.number}`} title="اتصال" data-testid={`company-phone-call-${pIdx}`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25">
                        <Phone size={11} weight="fill" />
                      </a>
                      <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" title="واتساب" data-testid={`company-phone-wa-${pIdx}`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#20BA5A]">
                        <WhatsappLogo size={11} weight="fill" className="text-white" />
                      </a>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => {
                  setPhonesDraft(hydratePhones(data.phones, data.phone));
                  setPhonesEditOpen(true);
                }}
                data-testid="company-phones-edit-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border border-primary/30 text-primary/80 hover:bg-primary/10"
              >
                <Pencil size={10} /> تعديل الأرقام
              </button>
            </div>
            {data.address && (
              <div className="mt-1 text-white/50 text-xs flex items-center gap-1.5">
                <MapPin size={12} />
                {data.address}
              </div>
            )}
          </div>
          {!isAdminCompany && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => updateStatus("approved")} data-testid="btn-approve" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#2E7D32]/15 border border-[#2E7D32]/50 text-[#2E7D32] hover:bg-[#2E7D32]/30 text-xs font-bold">
                <CheckCircle size={13} weight="fill" /> اعتماد
              </button>
              <button onClick={() => updateStatus("pending")} data-testid="btn-pending" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#F57F17]/50 text-[#F57F17] hover:bg-[#F57F17]/10 text-xs font-bold">
                <Clock size={13} weight="fill" /> قيد المراجعة
              </button>
              <button onClick={() => updateStatus("blocked")} data-testid="btn-block" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10 text-xs font-bold">
                <Prohibit size={13} weight="fill" /> حظر
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="إجمالي الحجوزات" value={data.stats?.total_bookings || 0} />
          <StatCard label="هذا الشهر" value={data.stats?.this_month || 0} />
          <StatCard label="قيد المراجعة" value={data.stats?.pending || 0} tone="amber" />
          <StatCard label="مكتملة" value={data.stats?.completed || 0} tone="green" />
        </div>

        {/* Pricing Template Link */}
        {!isAdminCompany && (
          <section
            className="rounded-2xl border border-primary/20 bg-card p-5"
            data-testid="company-pricing-template"
          >
            <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <FolderOpen size={20} weight="duotone" className="text-primary" />
                <div className="text-white font-bold text-lg">قالب الأسعار المرتبط</div>
                {data.pricing_template_id ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 border border-primary/50 text-primary font-bold">
                    مربوطة بقالب — أسعار حصرية
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3B82F6]/20 border border-[#3B82F6]/50 text-[#3B82F6] font-bold">
                    بدون قالب — تستخدم الأسعار العامة
                  </span>
                )}
              </div>
              <Link
                to="/admin/pricing-templates"
                data-testid="company-manage-templates-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 text-[11px] font-bold"
              >
                <FolderOpen size={12} weight="duotone" /> إدارة القوالب
              </Link>
            </div>

            <div
              className="mb-3 text-[12px] text-white/70 leading-relaxed flex items-start gap-1.5 rounded-lg bg-[#F57F17]/10 border border-[#F57F17]/30 p-3"
            >
              <Warning
                size={14}
                weight="fill"
                className="text-[#F57F17] shrink-0 mt-0.5"
              />
              <span>
                <strong className="text-[#F57F17]">تنبيه:</strong> عند ربط الشركة بقالب،
                سيتم استخدام أسعار هذا القالب <strong>حصرياً</strong>. إن لم يوجد سعر لمسار داخل
                القالب، لن يتم الرجوع إلى الأسعار العامة.
              </span>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="company-template-pills">
              {/* No-template pill */}
              <button
                type="button"
                disabled={templateSaving}
                onClick={() => {
                  if (!data.pricing_template_id) return;
                  setPricingTemplate(null);
                }}
                data-testid="company-template-pill-none"
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all disabled:opacity-50 ${
                  !data.pricing_template_id
                    ? "bg-[#3B82F6] text-white border-[#3B82F6] font-bold shadow-[0_0_0_2px_rgba(59,130,246,0.35)]"
                    : "bg-[#3B82F6]/5 border-[#3B82F6]/30 text-white hover:bg-[#3B82F6]/15"
                }`}
              >
                <TagIcon size={13} weight={!data.pricing_template_id ? "fill" : "duotone"} />
                بدون قالب (أسعار عامة)
              </button>

              {templates.map((t) => {
                const active = data.pricing_template_id === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={templateSaving}
                    onClick={() => {
                      if (active) return;
                      setPricingTemplate(t.id);
                    }}
                    data-testid={`company-template-pill-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all disabled:opacity-50 ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold shadow-[0_0_0_2px_rgba(201,169,97,0.4)]"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                    }`}
                    title={t.description || ""}
                  >
                    <FolderOpen size={13} weight={active ? "fill" : "duotone"} />
                    {t.name}
                    <span
                      className={`num text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        active
                          ? "bg-[#0A1936]/25 text-primary-foreground"
                          : "bg-white/10 text-white/70"
                      }`}
                    >
                      {fmtNum(t.rules_count || 0)}
                    </span>
                  </button>
                );
              })}

              {templates.length === 0 && (
                <div className="text-white/50 text-xs">
                  لا توجد قوالب حالياً — أنشئ قالباً من صفحة إدارة القوالب.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Company Clients — Admin CRUD */}
        <section className="rounded-2xl border border-primary/20 bg-card p-5" data-testid="company-clients">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="text-white font-bold text-lg flex items-center gap-2">
                <Buildings size={18} weight="duotone" className="text-primary" />
                عملاء الشركة
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary/15 border border-primary/40 text-primary num" dir="ltr">
                {fmtNum((data.clients || []).length)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setClientEdit({ idx: null, initial: null })}
                disabled={clientsBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-[#E1C484] text-primary-foreground text-[11px] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-add-client"
              >
                <Plus size={11} weight="bold" /> إضافة عميل
              </button>
              <button
                onClick={exportClientsExcel}
                disabled={clientsBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 text-[11px] font-bold disabled:opacity-50"
                data-testid="admin-export-clients"
              >
                <DownloadSimple size={11} weight="bold" /> تصدير Excel
              </button>
            </div>
          </div>

          {/* Search */}
          {(data.clients || []).length > 0 && (
            <div className="relative mb-3">
              <MagnifyingGlass size={14} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="search"
                value={clientsQ}
                onChange={(e) => setClientsQ(e.target.value)}
                placeholder="ابحث بالاسم أو الجوال أو البريد…"
                className="mz-input pr-9 text-sm"
                data-testid="admin-clients-search"
              />
            </div>
          )}

          {/* Grid */}
          {(data.clients || []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-primary/25 p-8 text-center text-white/50 text-sm">
              لا يوجد عملاء بعد — أضف عميلاً جديداً.
            </div>
          ) : clientsFiltered.length === 0 ? (
            <div className="text-center text-white/50 text-sm py-6">لا توجد نتائج مطابقة للبحث.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {clientsFiltered.map((c) => {
                const idx = data.clients.findIndex((x) => x.id === c.id);
                return (
                  <div
                    key={c.id || `${c.name}-${c.phone1}`}
                    className="rounded-xl border border-primary/15 bg-[#0F2148] p-4 hover:border-primary/40 transition-colors"
                    data-testid={`company-client-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-bold text-sm truncate">{c.name}</div>
                        {c.email && (
                          <div className="text-white/50 text-[11px] mt-0.5 inline-flex items-center gap-1 truncate" dir="ltr">
                            <Envelope size={10} className="text-primary/50" /> {c.email}
                          </div>
                        )}
                      </div>
                      <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary font-bold num" dir="ltr">
                        #{fmtNum(idx + 1)}
                      </span>
                    </div>
                    {c.phone1 && (
                      <div className="text-white/70 text-xs flex items-center gap-1.5 mb-0.5" dir="ltr">
                        <Phone size={11} className="text-primary/60" />
                        <span className="num">{formatPhoneDisplay(c.phone1)}</span>
                      </div>
                    )}
                    {c.phone2 && (
                      <div className="text-white/50 text-xs flex items-center gap-1.5 mb-2" dir="ltr">
                        <Phone size={11} className="text-primary/40" />
                        <span className="num">{formatPhoneDisplay(c.phone2)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-primary/10">
                      <button
                        onClick={() => setClientEdit({ idx, initial: c })}
                        className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-[11px] font-bold"
                        data-testid={`admin-edit-client-${idx}`}
                      >
                        <Pencil size={11} weight="bold" /> تعديل
                      </button>
                      <button
                        onClick={() => removeClient(idx, c.name)}
                        className="p-1.5 rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10"
                        data-testid={`admin-delete-client-${idx}`}
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Visible cars */}
        <section className="rounded-2xl border border-primary/20 bg-card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-white font-bold text-lg flex items-center gap-2">
                <CarIcon size={18} weight="duotone" className="text-primary" />
                السيارات المرئية لهذه الشركة
              </div>
              <div className="text-white/50 text-xs mt-1">
                {showAll
                  ? "لم يتم تحديد سيارات — الشركة ترى جميع السيارات (افتراضي)"
                  : `الشركة ترى ${fmtNum(visibleSet.size)} سيارة فقط`}
              </div>
            </div>
            {savingCars && <span className="text-primary text-xs animate-pulse">جارٍ الحفظ…</span>}
          </div>
          <div dir="rtl" className="flex flex-wrap gap-2" data-testid="visible-cars-toggle">
            {cars.map((c) => {
              const on = showAll || visibleSet.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCar(c.id)}
                  data-testid={`toggle-car-${c.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border transition-all ${
                    on
                      ? "bg-primary text-primary-foreground border-primary font-bold"
                      : "bg-white/5 border-white/20 text-white/60 hover:bg-white/10"
                  }`}
                >
                  {on ? <Check size={14} weight="bold" /> : <XIcon size={14} weight="bold" />}
                  {c.name_ar}
                </button>
              );
            })}
          </div>
          {!showAll && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.patch(`/admin/companies/${id}/visible-cars`, { car_ids: [] });
                  toast.success("تمت إعادة التعيين — الشركة ترى كل السيارات");
                  load();
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              data-testid="reset-visible-cars"
              className="mt-3 text-primary/80 hover:text-primary text-xs underline"
            >
              إعادة التعيين لعرض كل السيارات
            </button>
          )}
        </section>

        {/* Custom pricing */}
        <section className="rounded-2xl border border-primary/20 bg-card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-white font-bold text-lg flex items-center gap-2">
              <TagIcon size={18} weight="duotone" className="text-primary" />
              تسعير خاص لهذه الشركة
            </div>
            <button
              onClick={() => setPricingOpen(true)}
              data-testid="btn-add-custom-pricing"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-sm"
            >
              <Plus size={14} weight="bold" /> إضافة تسعير
            </button>
          </div>
          {(data.custom_pricing || []).length === 0 ? (
            <div className="text-center py-8 text-white/50 text-sm">لا يوجد تسعير خاص — سيتم استخدام الأسعار العامة</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.custom_pricing.map((p) => (
                <div key={p.id} data-testid={`custom-price-${p.id}`} className="rounded-xl border border-primary/15 bg-[#0F2148] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm">
                        {p.pickup} <span className="text-primary/70 mx-1">→</span> {p.destination}
                      </div>
                      <div className="text-white/60 text-xs mt-1">
                        {carName(p.car_type)}
                        {p.from_date && (
                          <span className="mr-2 num" dir="ltr">
                            · {fmtDate(p.from_date)} — {fmtDate(p.to_date)}
                          </span>
                        )}
                      </div>
                      {p.reason && <div className="text-primary/70 text-xs italic mt-1">&quot;{p.reason}&quot;</div>}
                    </div>
                    <div className="text-primary font-bold num text-lg">{fmtRiyal(p.price)}</div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-primary/10 flex justify-end">
                    <button
                      onClick={() => removePricing(p)}
                      data-testid={`delete-custom-price-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10 text-xs"
                    >
                      <Trash size={12} /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent bookings */}
        <section className="rounded-2xl border border-primary/20 bg-card p-5">
          <div className="text-white font-bold text-lg mb-3">آخر 10 حجوزات</div>
          {(data.recent_bookings || []).length === 0 ? (
            <div className="text-center py-8 text-white/50 text-sm">لا توجد حجوزات بعد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#0B1A3A] text-primary/80 text-xs">
                  <tr>
                    <th className="p-2 text-start">رقم الحجز</th>
                    <th className="p-2 text-start">التاريخ</th>
                    <th className="p-2 text-start">الوقت</th>
                    <th className="p-2 text-start">من</th>
                    <th className="p-2 text-start">إلى</th>
                    <th className="p-2 text-start">السيارة</th>
                    <th className="p-2 text-start">السعر</th>
                    <th className="p-2 text-start">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_bookings.map((b) => {
                    const iso = b.pickup_time || null;
                    return (
                      <tr key={b.id} className="border-t border-primary/10 hover:bg-primary/5">
                        <td className="p-2 num text-primary">{b.booking_number || "—"}</td>
                        <td className="p-2 num">{fmtDate(iso || b.trip_date)}</td>
                        <td className="p-2 num">{fmtTime(iso)}</td>
                        <td className="p-2">{b.pickup_location}</td>
                        <td className="p-2">{b.destination || b.dropoff_location}</td>
                        <td className="p-2">{b.car_type_name || b.car_type}</td>
                        <td className="p-2 num text-primary">{fmtNum(b.price || b.total_price)}</td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] border ${
                              b.status === "completed"
                                ? "text-[#1565C0] border-[#1565C0]/50 bg-[#1565C0]/10"
                                : b.status === "confirmed"
                                ? "text-[#2E7D32] border-[#2E7D32]/50 bg-[#2E7D32]/10"
                                : b.status === "rejected"
                                ? "text-[#C62828] border-[#C62828]/50 bg-[#C62828]/10"
                                : "text-[#F57F17] border-[#F57F17]/50 bg-[#F57F17]/10"
                            }`}
                          >
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <PricingModal
        open={pricingOpen}
        cars={cars}
        companyId={id}
        onClose={() => setPricingOpen(false)}
        onSaved={load}
      />

      {/* Client add/edit (admin) */}
      <ClientEditModal
        open={!!clientEdit}
        initial={clientEdit?.initial}
        title={clientEdit?.idx != null ? "تعديل عميل الشركة" : "عميل جديد للشركة"}
        onSave={saveClient}
        onClose={() => setClientEdit(null)}
      />

      {/* Edit company phones modal */}
      {phonesEditOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !phonesSaving && setPhonesEditOpen(false)}
          data-testid="company-phones-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0F1F42] border border-primary/30 rounded-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-primary/20">
              <h3 className="text-white font-bold">تعديل أرقام الشركة</h3>
              <button
                type="button"
                onClick={() => !phonesSaving && setPhonesEditOpen(false)}
                className="text-white/60 hover:text-white"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-4">
              <div className="text-xs text-white/60 mb-2">
                أضف أرقام تواصل متعددة. اضغط على النجمة لتعيين الرقم الأساسي.
              </div>
              <MultiPhoneInput
                value={phonesDraft}
                onChange={setPhonesDraft}
                testIdPrefix="company-phones-draft"
                maxEntries={5}
              />
            </div>
            <div className="p-4 border-t border-primary/20 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPhonesEditOpen(false)}
                disabled={phonesSaving}
                className="px-4 py-2 rounded-xl border border-white/20 text-white/80 hover:bg-white/5 text-sm"
              >
                إلغاء
              </button>
              <button
                type="button"
                data-testid="company-phones-save"
                disabled={phonesSaving}
                onClick={async () => {
                  const phoneError = validatePhonesList(phonesDraft);
                  if (phoneError) return toast.error(phoneError);
                  const cleaned = serializePhones(phonesDraft);
                  if (cleaned.length === 0) {
                    return toast.error("أدخل رقم واحد على الأقل");
                  }
                  setPhonesSaving(true);
                  try {
                    await api.patch(`/admin/companies/${id}`, { phones: cleaned });
                    toast.success("تم حفظ الأرقام");
                    setPhonesEditOpen(false);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "تعذر الحفظ");
                  } finally {
                    setPhonesSaving(false);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:brightness-110 disabled:opacity-50"
              >
                {phonesSaving ? "جارٍ الحفظ…" : "حفظ الأرقام"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
